import React from 'react';
import { Button, Typography, Divider, Collapse } from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  CarOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Customer, Vehicle } from '../../types';
import CustomerInlineForm from '../CustomerInlineForm';
import VehicleInlineForm from '../VehicleInlineForm';
import type { CustomerMatch } from '../../utils/customerMatcher';
import type { VehicleMatch } from '../../utils/vehicleMatcher';

const { Title, Text } = Typography;

export interface EstimateHeaderProps {
  isMobile: boolean;
  isEditing: boolean;
  selectedCustomer: Customer | null;
  selectedVehicle: Vehicle | null;
  onNavigateBack: () => void;
  onCustomerChange: (customer: Customer) => void;
  onVehicleChange: (vehicle: Vehicle) => void;
  onClearCustomer: () => void;
  onClearVehicle: () => void;
  onEditCustomer: () => void;
  onEditVehicle: () => void;
  customerCandidates: CustomerMatch[];
  vehicleCandidates: VehicleMatch[];
  onAcceptCustomerCandidate: (customer: Customer) => void;
  onDismissCustomerCandidates: () => void;
  onAcceptVehicleCandidate: (vehicle: Vehicle) => void;
  onDismissVehicleCandidates: () => void;
}

/** Header section: back navigation, title, customer/vehicle selection (mobile inline). */
export const EstimateHeader: React.FC<EstimateHeaderProps> = ({
  isMobile,
  isEditing,
  selectedCustomer,
  selectedVehicle,
  onNavigateBack,
  onCustomerChange,
  onVehicleChange,
  onClearCustomer,
  onClearVehicle,
  onEditCustomer,
  onEditVehicle,
  customerCandidates,
  vehicleCandidates,
  onAcceptCustomerCandidate,
  onDismissCustomerCandidates,
  onAcceptVehicleCandidate,
  onDismissVehicleCandidates,
}) => {
  if (isMobile) {
    return (
      <>
        {/* Mobile top bar */}
        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onNavigateBack}
            style={{ padding: '4px 0', height: 'auto', fontWeight: 500 }}
          >
            Back
          </Button>
          <Title level={5} style={{ margin: 0 }}>
            {isEditing ? 'Edit Estimate' : 'New Estimate'}
          </Title>
          <div style={{ width: 60 }} />
        </div>

        {/* Mobile inline customer/vehicle */}
        <div style={{ marginBottom: 16 }}>
          {/* Customer/Vehicle summary when selected */}
          {(selectedCustomer || selectedVehicle) && (
            <div style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f9fafb', paddingBottom: 4 }}>
              {selectedCustomer && (
                <div style={{ padding: '6px 10px', background: '#f0f7ff', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontSize: 13 }}>
                  <span>
                    <UserOutlined style={{ color: '#3b82f6', marginRight: 6 }} />
                    <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong> — {selectedCustomer.phone || selectedCustomer.email}
                  </span>
                  <span>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={onEditCustomer} style={{ padding: '0 4px' }}>Edit</Button>
                    <Button type="link" size="small" onClick={onClearCustomer} style={{ padding: '0 4px' }}>Change</Button>
                  </span>
                </div>
              )}
              {selectedVehicle && (
                <div style={{ padding: '6px 10px', background: '#fffbeb', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontSize: 13 }}>
                  <span>
                    <CarOutlined style={{ color: '#f59e0b', marginRight: 6 }} />
                    <strong>{selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}</strong> — {selectedVehicle.current_mileage?.toLocaleString()} mi
                  </span>
                  <span>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={onEditVehicle} style={{ padding: '0 4px' }}>Edit</Button>
                    <Button type="link" size="small" onClick={onClearVehicle} style={{ padding: '0 4px' }}>Change</Button>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Expandable forms for unselected customer/vehicle */}
          <Collapse
            ghost
            defaultActiveKey={(!selectedCustomer || !selectedVehicle) ? [!selectedCustomer ? 'customer' : '', !selectedVehicle ? 'vehicle' : ''].filter(Boolean) : []}
            style={{ marginBottom: 10 }}
            items={[
              ...(!selectedCustomer ? [{
                key: 'customer',
                label: <span style={{ fontSize: 14, fontWeight: 600 }}><UserOutlined style={{ color: '#3b82f6', marginRight: 6 }} />Customer</span>,
                children: (
                  <CustomerInlineForm
                    value={selectedCustomer || undefined}
                    onChange={onCustomerChange}
                    aiCustomerCandidates={customerCandidates}
                    onAcceptCandidate={onAcceptCustomerCandidate}
                    onDismissCandidates={onDismissCustomerCandidates}
                  />
                ),
              }] : []),
              ...(!selectedVehicle ? [{
                key: 'vehicle',
                label: <span style={{ fontSize: 14, fontWeight: 600 }}><CarOutlined style={{ color: '#f59e0b', marginRight: 6 }} />Vehicle</span>,
                children: (
                  <VehicleInlineForm
                    customerId={selectedCustomer?.id}
                    customerName={selectedCustomer
                      ? (selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`)
                      : undefined
                    }
                    value={selectedVehicle || undefined}
                    onChange={onVehicleChange}
                    aiVehicleCandidates={vehicleCandidates}
                    onAcceptVehicleCandidate={onAcceptVehicleCandidate}
                    onDismissVehicleCandidates={onDismissVehicleCandidates}
                  />
                ),
              }] : []),
            ]}
          />
        </div>
      </>
    );
  }

  // Desktop header
  return (
    <div style={{
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
      zIndex: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onNavigateBack}>
          Back to Estimates
        </Button>
        <Divider type="vertical" />
        <Title level={3} style={{ margin: 0 }}>
          {isEditing ? 'Edit Estimate' : 'New Estimate'}
        </Title>
        {selectedCustomer && selectedVehicle && (
          <>
            <Divider type="vertical" />
            <Text type="secondary">
              {selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`} &bull;{' '}
              {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};
